import { useEffect, useMemo, useRef, useState, memo } from "react";
import { Loader2, Maximize, Minimize, Pause, Play, Volume2, VolumeX, RotateCcw } from "lucide-react";
import Hls from "hls.js";

const formatTime = (secs) => {
  if (!isFinite(secs) || secs < 0) return "0:00";
  const minutes = Math.floor(secs / 60);
  const seconds = Math.floor(secs % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const normalizeBunnySource = (rawSrc) => {
  if (rawSrc === undefined || rawSrc === null) {
    return { mode: "none", url: "" };
  }

  const str = String(rawSrc).trim();
  if (!str) return { mode: "none", url: "" };

  const iframeMatch = str.match(/<iframe[^>]*src=["']([^"']+)["']/i);
  if (iframeMatch?.[1]) {
    return { mode: "iframe", url: iframeMatch[1] };
  }

  const lower = str.toLowerCase();
  if (lower.includes("/embed/") || lower.includes("bunnycdn") || lower.includes("mediadelivery")) {
    return { mode: "iframe", url: str };
  }

  if (lower.includes(".m3u8") || lower.includes("playlist.m3u8")) {
    return { mode: "hls", url: str };
  }

  if (/\.(mp4|webm|mov|mkv)(\?|$)/i.test(lower)) {
    return { mode: "file", url: str };
  }

  return { mode: "file", url: str };
};

function BunnyPlayer({ src, title, userEmail }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const hideControlsTimer = useRef(null);

  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [errorMessage, setErrorMessage] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [autoRetryCount, setAutoRetryCount] = useState(0);
  const [levels, setLevels] = useState([]);
  const [selectedQuality, setSelectedQuality] = useState("auto");
  const [playbackRate, setPlaybackRate] = useState(1);
  const [emailPosition, setEmailPosition] = useState({
    top: "15%",
    left: "15%",
    opacity: 0,
  });

  const source = useMemo(() => normalizeBunnySource(src), [src]);
  const videoUrl = source.mode === "hls" || source.mode === "file" ? source.url : "";
  const iframeUrl = source.mode === "iframe" ? source.url : "";
  const progress = duration ? (currentTime / duration) * 100 : 0;
  const currentLabel = formatTime(currentTime);
  const durationLabel = formatTime(duration);

  const resetUiState = () => {
    setStatus(videoUrl ? "loading" : source.mode === "iframe" ? "ready" : "idle");
    setErrorMessage("");
    setIsPlaying(false);
    setIsMuted(false);
    setVolume(1);
    setDuration(0);
    setCurrentTime(0);
    setIsBuffering(false);
    setLevels([]);
    setSelectedQuality("auto");
    setPlaybackRate(1);
    setAutoRetryCount(0);
    setEmailPosition((prev) => ({ ...prev, opacity: 1 }));
  };

  useEffect(() => {
    resetUiState();
  }, [source.mode, source.url]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    if (source.mode === "iframe" || source.mode === "none" || !videoUrl) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    setStatus("loading");
    setErrorMessage("");
    setIsBuffering(false);

    video.controls = false;
    video.loop = false;
    video.preload = "auto";
    video.muted = isMuted;
    video.volume = volume;
    video.playbackRate = playbackRate;

    const handleLoadedMetadata = () => {
      setDuration(video.duration || 0);
      setStatus("ready");
      setIsBuffering(false);
      if (autoRetryCount !== 0) {
        setAutoRetryCount(0);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setIsBuffering(false);
      startHideControlsTimer();
    };

    const handlePause = () => {
      setIsPlaying(false);
      setShowControls(true);
      clearHideControlsTimer();
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (video.duration) {
        setDuration(video.duration);
      }
    };

    const handleWaiting = () => {
      if (status === "ready") {
        setIsBuffering(true);
      }
    };

    const handleCanPlay = () => setIsBuffering(false);

    const handleEnded = () => {
      setIsPlaying(false);
      setShowControls(true);
    };

    const handleError = () => {
      setIsPlaying(false);
      setIsBuffering(false);
      if (autoRetryCount < 2) {
        setAutoRetryCount((prev) => prev + 1);
        setReloadKey((prev) => prev + 1);
        return;
      }
      setStatus("error");
      setErrorMessage("Video failed to load. Please try again.");
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("error", handleError);

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const attachSource = () => {
      if (source.mode === "hls" && Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
        hls.loadSource(videoUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          const rawLevels = hls.levels || [];
          const mapped = rawLevels.map((lvl, index) => {
            const height = lvl.height || lvl.attrs?.RESOLUTION?.split("x")[1];
            const bitrate = lvl.bitrate || lvl.attrs?.BANDWIDTH;
            return {
              index,
              label: `${height || "L" + index}${bitrate ? ` • ${(bitrate / 1_000_000).toFixed(1)} Mbps` : ""}`,
            };
          });
          setLevels(mapped);
          setSelectedQuality("auto");
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data?.fatal) {
            setStatus("error");
            setErrorMessage("Network error while loading video.");
            hls.destroy();
          }
        });
        hlsRef.current = hls;
      } else if (source.mode === "hls" && video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = videoUrl;
      } else {
        video.src = videoUrl;
      }
    };

    attachSource();

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("error", handleError);

      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoUrl, source.mode, reloadKey, isMuted, volume]);

  useEffect(() => {
    const handleFsChange = () => {
      const fsElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(Boolean(fsElement));
    };

    document.addEventListener("fullscreenchange", handleFsChange);
    document.addEventListener("webkitfullscreenchange", handleFsChange);
    document.addEventListener("mozfullscreenchange", handleFsChange);
    document.addEventListener("MSFullscreenChange", handleFsChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFsChange);
      document.removeEventListener("webkitfullscreenchange", handleFsChange);
      document.removeEventListener("mozfullscreenchange", handleFsChange);
      document.removeEventListener("MSFullscreenChange", handleFsChange);
    };
  }, []);

  useEffect(() => {
    return () => clearHideControlsTimer();
  }, []);

  useEffect(() => {
    if (!videoUrl) return;

    const handleKey = (event) => {
      if (["INPUT", "TEXTAREA"].includes(event.target.tagName)) return;
      switch (event.key.toLowerCase()) {
        case " ":
        case "k":
          event.preventDefault();
          togglePlay(event);
          break;
        case "m":
          toggleMute(event);
          break;
        case "f":
          toggleFullscreen(event);
          break;
        case "arrowleft":
          event.preventDefault();
          seekRelative(-5);
          break;
        case "arrowright":
          event.preventDefault();
          seekRelative(5);
          break;
        case "arrowup":
          event.preventDefault();
          changeVolume(Math.min(1, volume + 0.1));
          break;
        case "arrowdown":
          event.preventDefault();
          changeVolume(Math.max(0, volume - 0.1));
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [videoUrl, volume]);

  const clearHideControlsTimer = () => {
    if (hideControlsTimer.current) {
      clearTimeout(hideControlsTimer.current);
      hideControlsTimer.current = null;
    }
  };

  const startHideControlsTimer = () => {
    clearHideControlsTimer();
    hideControlsTimer.current = setTimeout(() => setShowControls(false), 2800);
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (isPlaying) {
      startHideControlsTimer();
    }
  };

  useEffect(() => {
    if (!userEmail) return;
    const interval = setInterval(() => {
      setEmailPosition({
        top: `${Math.floor(Math.random() * 70) + 10}%`,
        left: `${Math.floor(Math.random() * 70) + 10}%`,
        opacity: 0.9,
      });
      setTimeout(() => {
        setEmailPosition((prev) => ({ ...prev, opacity: 1 }));
      }, 2200);
    }, 6500);
    return () => clearInterval(interval);
  }, [userEmail]);

  const togglePlay = (event) => {
    if (event) event.stopPropagation();
    const video = videoRef.current;
    if (!video || source.mode === "iframe") return;

    if (video.paused) {
      video
        .play()
        .then(() => {
          setIsPlaying(true);
          startHideControlsTimer();
        })
        .catch(() => {
          setStatus("error");
          setErrorMessage("Unable to start playback.");
        });
    } else {
      video.pause();
    }
  };

  const toggleMute = (event) => {
    if (event) event.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
    if (!video.muted && video.volume === 0) {
      video.volume = 0.4;
      setVolume(0.4);
    }
  };

  const changeVolume = (value) => {
    const video = videoRef.current;
    if (!video) return;
    const next = Math.min(1, Math.max(0, value));
    video.volume = next;
    video.muted = next === 0;
    setVolume(next);
    setIsMuted(video.muted);
  };

  const handleSeek = (value) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    const pct = Math.min(100, Math.max(0, Number(value)));
    video.currentTime = (pct / 100) * duration;
    setCurrentTime(video.currentTime);
  };

  const seekRelative = (deltaSeconds) => {
    const video = videoRef.current;
    if (!video) return;
    const nextTime = Math.min(Math.max(video.currentTime + deltaSeconds, 0), video.duration || video.currentTime);
    video.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const toggleFullscreen = (event) => {
    if (event) event.stopPropagation();
    const target = containerRef.current;
    if (!target) return;

    const fsElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (!fsElement) {
      if (target.requestFullscreen) target.requestFullscreen();
      else if (target.webkitRequestFullscreen) target.webkitRequestFullscreen();
      else if (target.mozRequestFullScreen) target.mozRequestFullScreen();
      else if (target.msRequestFullscreen) target.msRequestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  };

  const handleRetry = (event) => {
    if (event) event.stopPropagation();
    setAutoRetryCount(0);
    setReloadKey((prev) => prev + 1);
    setStatus("loading");
  };

  const handleQualityChange = (event) => {
    const value = event.target.value;
    setSelectedQuality(value);
    const hls = hlsRef.current;
    if (!hls) return;
    if (value === "auto") {
      hls.currentLevel = -1;
      return;
    }
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed)) {
      hls.currentLevel = parsed;
    }
  };

  const handleSpeedChange = (event) => {
    const value = parseFloat(event.target.value);
    if (Number.isNaN(value)) return;
    setPlaybackRate(value);
  };

  if (!videoUrl && !iframeUrl) {
    return (
      <div
        style={{
          width: "100%",
          padding: "40px",
          background: "#0f1117",
          color: "white",
          borderRadius: "16px",
          textAlign: "center",
        }}
      >
        No video source available.
      </div>
    );
  }

  const containerStyle = {
    width: "100%",
    position: "relative",
    borderRadius: isFullscreen ? "0px" : "32px",
    background: "radial-gradient(circle at 15% 20%, rgba(96,165,250,0.2), transparent 45%), radial-gradient(circle at 80% 0%, rgba(236,72,153,0.18), transparent 55%), #020617",
    aspectRatio: isFullscreen ? "auto" : "16 / 9",
    overflow: "hidden",
    boxShadow: "0 35px 80px rgba(5,10,25,0.65)",
    border: isFullscreen ? "none" : "1px solid rgba(255,255,255,0.05)",
  };

  const blurOrb = (styleOverride) => ({
    position: "absolute",
    borderRadius: "50%",
    filter: "blur(60px)",
    opacity: 0.65,
    pointerEvents: "none",
    ...styleOverride,
  });

  if (iframeUrl) {
    return (
      <div className="custom-bunny-player" style={{ width: "100%" }}>
        <div ref={containerRef} style={containerStyle}>
          {title ? (
            <div
              style={{
                position: "absolute",
                top: 16,
                left: 20,
                color: "rgba(255,255,255,0.85)",
                fontWeight: 600,
                zIndex: 5,
              }}
            >
              {title}
            </div>
          ) : null}
          <iframe
            title={title || "Course video"}
            src={iframeUrl}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            style={{
              width: "100%",
              height: "100%",
              border: "0",
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="custom-bunny-player w-full max-w-5xl mx-auto rounded-3xl overflow-hidden bg-slate-950/90 shadow-[0_20px_60px_rgba(15,23,42,0.85)] border border-white/5">
    <div
      ref={containerRef}
      style={containerStyle}
      className="relative w-full h-full bg-black"
      onMouseMove={handleMouseMove}
      onClick={togglePlay}
    >
      {/* Top badge bar */}
      {title ? (
        <div
          className="
            absolute top-4 left-4 right-4 z-20
            flex flex-wrap items-center justify-between gap-3
          "
        >
          <div
            className="
              inline-flex items-center gap-2
              px-4 py-2
              rounded-full
              text-white font-semibold tracking-wide
              shadow-[0_14px_35px_rgba(59,130,246,0.7)]
              bg-gradient-to-r from-blue-500/95 via-indigo-500/90 to-violet-500/90
              backdrop-blur
            "
          >
            <span className="text-xs sm:text-sm">{title}</span>
            <span
              className="
                text-[9px] uppercase tracking-[0.18em]
                px-2 py-1 rounded-full
                bg-white/15 border border-white/40
                shadow-sm
              "
            >
              Secure Player
            </span>
          </div>
  
          <div
            className="
              inline-flex items-center gap-2
              px-3.5 py-2
              rounded-2xl
              bg-slate-950/70 text-white/85 text-[10px] sm:text-xs
              uppercase tracking-[0.22em]
              border border-white/10 shadow-[0_10px_28px_rgba(15,23,42,0.85)]
              backdrop-blur
            "
          >
            <span>HD</span>
            <span className="w-1 h-1 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
            <span>{levels.length ? "Adaptive" : "Standard"}</span>
          </div>
        </div>
      ) : null}
  
      {/* Video */}
      <video
        ref={videoRef}
        playsInline
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          background: "black",
        }}
        onClick={(event) => event.stopPropagation()}
      />
  
      {/* Loading */}
      {(status === "loading" || isBuffering) && (
        <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm bg-black/40">
          <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-white animate-spin" />
        </div>
      )}
  
      {/* Error */}
      {status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/75 text-white gap-4 px-6 text-center">
          <p className="text-sm sm:text-base opacity-90">{errorMessage}</p>
          <button
            onClick={handleRetry}
            className="
              inline-flex items-center gap-2
              px-4 py-2 rounded-full
              bg-white/10 border border-white/30
              hover:bg-white/20 transition
              text-xs sm:text-sm
              backdrop-blur
            "
          >
            <RotateCcw size={16} />
            Retry
          </button>
        </div>
      )}
  
      {/* Center play button */}
      {!isPlaying && status === "ready" && !isBuffering && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <button
            onClick={togglePlay}
            className="
              pointer-events-auto
              inline-flex items-center justify-center
              w-14 h-14 sm:w-20 sm:h-20
              rounded-full
              bg-gradient-to-br from-indigo-500 via-blue-500 to-sky-400
              text-white
              shadow-[0_20px_60px_rgba(37,99,235,0.8)]
              hover:scale-110 active:scale-95
              transition-transform
            "
          >
            <Play size={24} className="ml-0.5 sm:ml-1 sm:size-7" fill="currentColor" />
          </button>
        </div>
      )}
  
      {/* Watermark email */}
      {userEmail && (
        <div
          style={{
            position: "absolute",
            top: emailPosition.top,
            left: emailPosition.left,
            transform: "translate(-50%, -50%)",
            opacity: emailPosition.opacity,
          }}
          className="
            px-3.5 py-1.5
            rounded-full
            bg-black/65 text-white
            text-[10px] sm:text-xs tracking-[0.04em]
            pointer-events-none
            transition-opacity duration-400
            border border-white/15 backdrop-blur-sm z-20
            shadow-[0_8px_24px_rgba(15,23,42,0.9)]
          "
        >
          {userEmail}
        </div>
      )}
  
      {/* Controls overlay */}
      <div
        className={`
          absolute inset-0 flex flex-col justify-end
          transition-all duration-300
          ${showControls ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}
        `}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Gradient behind controls */}
        <div
          className={`
            pointer-events-none absolute inset-0
            ${isFullscreen ? "rounded-none" : "rounded-t-[28px] sm:rounded-t-[36px]"}
            border-t border-white/5
          `}
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(2,6,23,0.65) 45%, rgba(2,6,23,0.98) 100%)",
          }}
        />
  
        <div
          className={`
            relative w-full max-w-full mx-auto
            px-4 sm:px-6 md:px-8
            pb-4 sm:pb-6 md:pb-7
            pt-16 sm:pt-20 md:pt-24
            space-y-3 sm:space-y-4
          `}
        >
          {/* Time & progress */}
          <div className="flex items-center gap-3 text-white text-xs sm:text-sm flex-wrap">
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5 shadow-inner shadow-black/40 backdrop-blur">
              <span className="font-mono">{currentLabel}</span>
              <span className="text-white/50 text-[10px]">/</span>
              <span className="font-mono text-white/70">{durationLabel}</span>
            </div>
            <div className="flex-1 w-full min-w-[180px]">
              <input
                type="range"
                min={0}
                max={100}
                step={0.1}
                value={progress}
                onChange={(e) => handleSeek(e.target.value)}
                className="w-full h-1.5 accent-blue-500 custom-slider"
              />
            </div>
          </div>
  
          {/* Bottom row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 sm:gap-4 text-white">
            {/* Left: play & skip */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 bg-gradient-to-r from-blue-500/30 via-indigo-500/25 to-violet-500/20 rounded-2xl px-2 py-2 border border-white/10 shadow-lg shadow-blue-900/40 backdrop-blur">
                <button
                  onClick={togglePlay}
                  className="
                    w-10 h-10 sm:w-11 sm:h-11
                    rounded-full
                    bg-gradient-to-r from-blue-500 to-indigo-600
                    flex items-center justify-center
                    shadow-[0_14px_40px_rgba(37,99,235,0.6)]
                    hover:scale-110 active:scale-95
                    transition-transform
                  "
                >
                  {isPlaying ? (
                    <Pause size={18} className="sm:size-5" />
                  ) : (
                    <Play size={18} className="ml-0.5 sm:ml-1 sm:size-5" fill="currentColor" />
                  )}
                </button>
                <div className="flex items-center gap-1 text-[10px] sm:text-xs pr-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      seekRelative(-10);
                    }}
                    className="
                      px-3 py-1 rounded-full
                      bg-white/10 border border-white/15
                      hover:bg-white/20 transition
                    "
                  >
                    -10s
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      seekRelative(10);
                    }}
                    className="
                      px-3 py-1 rounded-full
                      bg-white/10 border border-white/15
                      hover:bg-white/20 transition
                    "
                  >
                    +10s
                  </button>
                </div>
              </div>
            </div>
  
            {/* Right: volume, fullscreen, quality/speed */}
            <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
              {/* Volume */}
              <div className="flex items-center gap-3 bg-white/5 rounded-2xl px-3 py-2 border border-white/10 flex-wrap backdrop-blur">
                <button
                  onClick={toggleMute}
                  className="
                    w-9 h-9 sm:w-11 sm:h-11
                    rounded-full bg-white/10 border border-white/20
                    flex items-center justify-center
                    hover:bg-white/20 transition
                  "
                >
                  {isMuted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <div className="w-24 sm:w-32 md:w-40">
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={isMuted ? 0 : volume}
                    onChange={(e) => changeVolume(parseFloat(e.target.value))}
                    className="w-full accent-blue-500 custom-slider"
                  />
                </div>
              </div>
  
              {/* Fullscreen */}
              <button
                onClick={toggleFullscreen}
                className="
                  w-9 h-9 sm:w-11 sm:h-11
                  rounded-full bg-white/10 border border-white/20
                  flex items-center justify-center
                  hover:bg-white/20 transition
                  backdrop-blur
                "
              >
                {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
              </button>
  
              {/* Quality & Speed */}
              <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs bg-white/5 px-3 sm:px-4 py-2 rounded-full border border-white/10 backdrop-blur">
                {levels.length > 0 && (
                  <>
                    <span className="text-white/70 hidden sm:inline">Quality</span>
                    <select
                      value={selectedQuality}
                      onChange={handleQualityChange}
                      className="
                        bg-black/40 border border-white/30 rounded
                        px-2 py-1 text-white text-[10px] sm:text-xs
                        focus:outline-none focus:ring-1 focus:ring-white/40
                      "
                    >
                      <option value="auto">Auto</option>
                      {levels.map((lvl) => (
                        <option key={lvl.index} value={lvl.index}>
                          {lvl.label}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <span className="text-white/70 hidden sm:inline">Speed</span>
                <select
                  value={playbackRate}
                  onChange={handleSpeedChange}
                  className="
                    bg-black/40 border border-white/30 rounded
                    px-2 py-1 text-white text-[10px] sm:text-xs
                    focus:outline-none focus:ring-1 focus:ring-white/40
                  "
                >
                  <option value={0.5}>0.5x</option>
                  <option value={0.75}>0.75x</option>
                  <option value={1}>1x</option>
                  <option value={1.25}>1.25x</option>
                  <option value={1.5}>1.5x</option>
                  <option value={2}>2x</option>
                </select>
              </div>
            </div>
          </div>
  
          {/* Slider styling */}
          <style>{`
            .custom-slider {
              appearance: none;
              height: 4px;
              background: linear-gradient(90deg, rgba(59,130,246,0.85), rgba(147,51,234,0.9));
              border-radius: 999px;
              outline: none;
            }
            .custom-slider::-webkit-slider-thumb {
              appearance: none;
              width: 14px;
              height: 14px;
              border-radius: 50%;
              background: white;
              border: 3px solid rgba(59,130,246,0.95);
              box-shadow: 0 4px 14px rgba(59,130,246,0.7);
            }
            .custom-slider::-moz-range-thumb {
              width: 14px;
              height: 14px;
              border-radius: 50%;
              background: white;
              border: 3px solid rgba(59,130,246,0.95);
              box-shadow: 0 4px 14px rgba(59,130,246,0.7);
            }
            @media (max-width: 640px) {
              .custom-slider {
                height: 3px;
              }
            }
          `}</style>
        </div>
      </div>
    </div>
  </div>
  
  );
}

export default memo(BunnyPlayer);


